import type { DeleteConfirmState } from "@bundle/core";
import { Button, Sheet, Stack, Text } from "../primitives/index.ts";
import { useT } from "../i18n/I18nProvider.tsx";

export type DeleteMessageDialogProps = {
  /** ChannelViewModel `deleting`; absent = closed. */
  confirm: DeleteConfirmState | undefined;
  onConfirm: () => void;
  onCancel: () => void;
  testID: string;
};

/**
 * "Delete message?" (Slack-style): the message quoted (author and a one-line preview), what else goes with it (its
 * files; someone else's message when an admin deletes it) and what stays (a thread root's replies), Cancel / Delete.
 */
export function DeleteMessageDialog({ confirm, onConfirm, onCancel, testID }: DeleteMessageDialogProps) {
  const t = useT();
  return (
    <Sheet open={confirm !== undefined} onClose={onCancel} title={t("message.deleteConfirm.title")} detents={["medium"]} closeLabel={t("common.close")} testID={testID}>
      {confirm === undefined ? null : (
        <Stack gap="4">
          <Text color="text.secondary">{t("message.deleteConfirm.body")}</Text>
          <Stack gap="1" padding="3" radius="md" border="border.subtle" background="bg.surface" testID={`${testID}-preview`}>
            <Text weight="semibold" lines={1}>
              {confirm.authorName}
            </Text>
            {confirm.preview === "" ? null : (
              <Text color="text.secondary" lines={3}>
                {confirm.preview}
              </Text>
            )}
          </Stack>
          {confirm.others ? (
            <Text variant="footnote" color="text.secondary" testID={`${testID}-others`}>
              {t("message.deleteConfirm.others", { name: confirm.authorName })}
            </Text>
          ) : null}
          {confirm.fileCount > 0 ? (
            <Text variant="footnote" color="text.secondary" testID={`${testID}-files`}>
              {t("message.deleteConfirm.files", { count: confirm.fileCount })}
            </Text>
          ) : null}
          {confirm.hasReplies ? (
            <Text variant="footnote" color="text.secondary" testID={`${testID}-replies`}>
              {t("message.deleteConfirm.replies")}
            </Text>
          ) : null}
          <Stack direction="horizontal" gap="2" justify="end">
            <Button label={t("message.deleteConfirm.cancel")} variant="secondary" onPress={onCancel} testID={`${testID}-cancel`} />
            <Button label={t("message.deleteConfirm.confirm")} variant="destructive" onPress={onConfirm} testID={`${testID}-confirm`} />
          </Stack>
        </Stack>
      )}
    </Sheet>
  );
}
